export type CategoryKey =
  | 'business'
  | 'transport'
  | 'restaurants'
  | 'retail'
  | 'services'
  | 'tourism'

export interface CategorySpendingRow {
  id: string
  key: CategoryKey
  name: string
  amount: number
  percentage: number
  color: string
}

interface CategoryConfig {
  key: CategoryKey
  color: string
  label: string
}

const normalizeKey = (value?: string) => {
  if (!value) return ''
  return value.toLowerCase().replace(/[^a-z]/g, '')
}

export const buildCategoryRows = (
  categoryData: Array<{ id?: string; name?: string; slug?: string; amount?: string | number }> | undefined,
  labels: Record<CategoryKey, string>,
  colors: Record<CategoryKey, string>
): CategorySpendingRow[] => {
  const data = Array.isArray(categoryData) ? categoryData : []
  const mapped = new Map<string, { id?: string; amount: number }>()

  data.forEach((item) => {
    const key = normalizeKey(item.slug || item.name)
    const amount = typeof item.amount === 'string' ? Number(item.amount) : Number(item.amount || 0)
    if (!key) return
    mapped.set(key, { id: item.id, amount: Number.isFinite(amount) ? amount : 0 })
  })

  const configs: CategoryConfig[] = [
    { key: 'business', color: colors.business, label: labels.business },
    { key: 'transport', color: colors.transport, label: labels.transport },
    { key: 'restaurants', color: colors.restaurants, label: labels.restaurants },
    { key: 'retail', color: colors.retail, label: labels.retail },
    { key: 'services', color: colors.services, label: labels.services },
    { key: 'tourism', color: colors.tourism, label: labels.tourism },
  ]

  const rows = configs.map((config) => {
    const lookup = mapped.get(config.key)
    return {
      id: lookup?.id || config.key,
      key: config.key,
      name: config.label,
      amount: lookup?.amount ?? 0,
      percentage: 0,
      color: config.color,
    }
  })

  const total = rows.reduce((sum, row) => sum + row.amount, 0)
  return rows.map((row) => ({
    ...row,
    percentage: total > 0 ? (row.amount / total) * 100 : 0,
  }))
}
